import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// Threshold in pixels to account for rounding errors in scroll calculations
const SCROLL_THRESHOLD = 2;

type UseCardCarouselProps = {
	itemCount: number;
};

type UseCardCarouselReturn = {
	scrollContainerRef: React.RefObject<HTMLDivElement>;
	itemRefs: React.MutableRefObject<Array<HTMLDivElement | null>>;
	activeIndex: number;
	canScrollLeft: boolean;
	canScrollRight: boolean;
	needsScroll: boolean;
	handleScrollLeft: () => void;
	handleScrollRight: () => void;
	setItemRef: (index: number) => (el: HTMLDivElement | null) => void;
};

function findClosestItemIndex(
	container: HTMLDivElement,
	itemRefs: React.MutableRefObject<Array<HTMLDivElement | null>>,
): number {
	const containerCenter = container.scrollLeft + container.offsetWidth / 2;

	let closestIndex = 0;
	let smallestDistance = Infinity;

	itemRefs.current.forEach((el, index) => {
		if (!el) return;

		const itemCenter = el.offsetLeft + el.offsetWidth / 2;
		const distance = Math.abs(containerCenter - itemCenter);

		if (distance < smallestDistance) {
			smallestDistance = distance;
			closestIndex = index;
		}
	});

	return closestIndex;
}

function createScrollHandler(
	scrollTimeoutRef: React.MutableRefObject<NodeJS.Timeout | null>,
	updateActiveIndex: () => void,
): () => void {
	return () => {
		const timeoutRef = scrollTimeoutRef;
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
		}

		timeoutRef.current = setTimeout(() => {
			updateActiveIndex();
		}, 100);
	};
}

function setupScrollListener(
	container: HTMLDivElement,
	scrollTimeoutRef: React.MutableRefObject<NodeJS.Timeout | null>,
	updateActiveIndex: () => void,
) {
	const handleScroll = createScrollHandler(scrollTimeoutRef, updateActiveIndex);
	const timeoutRef = scrollTimeoutRef;

	container.addEventListener('scroll', handleScroll);
	updateActiveIndex();

	return () => {
		container.removeEventListener('scroll', handleScroll);
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
		}
	};
}

function createScrollHandlers(activeIndex: number, itemCount: number, scrollToItem: (index: number) => void) {
	return {
		handleScrollLeft: () => {
			if (activeIndex > 0) {
				scrollToItem(activeIndex - 1);
			}
		},
		handleScrollRight: () => {
			if (activeIndex < itemCount - 1) {
				scrollToItem(activeIndex + 1);
			}
		},
	};
}

function useOverflowDetection(containerRef: React.RefObject<HTMLDivElement>): boolean {
	const [needsScroll, setNeedsScroll] = useState(false);

	useEffect(() => {
		const container = containerRef.current;
		if (!container) return undefined;

		const checkOverflow = () => {
			const overflow = container.scrollWidth - container.clientWidth;
			setNeedsScroll(overflow > SCROLL_THRESHOLD);
		};

		checkOverflow();

		// Check on container resize
		const resizeObserver = new ResizeObserver(checkOverflow);
		resizeObserver.observe(container);

		window.addEventListener('resize', checkOverflow);

		return () => {
			resizeObserver.disconnect();
			window.removeEventListener('resize', checkOverflow);
		};
	}, [containerRef]);

	return needsScroll;
}

function useScrollState(containerRef: React.RefObject<HTMLDivElement>) {
	const [canScrollLeft, setCanScrollLeft] = useState(false);
	const [canScrollRight, setCanScrollRight] = useState(false);

	const updateScrollState = useCallback(() => {
		const container = containerRef.current;
		if (!container) return;

		// Check if we can scroll left (not at the start)
		setCanScrollLeft(container.scrollLeft > SCROLL_THRESHOLD);

		// Check if we can scroll right (not at the end)
		const maxScrollLeft = container.scrollWidth - container.clientWidth;
		setCanScrollRight(container.scrollLeft < maxScrollLeft - SCROLL_THRESHOLD);
	}, [containerRef]);

	useEffect(() => {
		// Update scroll state on window resize
		window.addEventListener('resize', updateScrollState);
		return () => {
			window.removeEventListener('resize', updateScrollState);
		};
	}, [updateScrollState]);

	return { canScrollLeft, canScrollRight, updateScrollState };
}

export function useCardCarousel({ itemCount }: UseCardCarouselProps): UseCardCarouselReturn {
	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const itemRefs = useRef<Array<HTMLDivElement | null>>([]);
	const [activeIndex, setActiveIndex] = useState(0);
	const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

	const needsScroll = useOverflowDetection(scrollContainerRef);
	const { canScrollLeft, canScrollRight, updateScrollState } = useScrollState(scrollContainerRef);

	const updateActiveIndex = useCallback(() => {
		const container = scrollContainerRef.current;
		if (!container || container.clientWidth === 0) return;
		const closestIndex = findClosestItemIndex(container, itemRefs);
		setActiveIndex(closestIndex);
		updateScrollState();
	}, [updateScrollState]);

	useEffect(() => {
		const container = scrollContainerRef.current;
		if (!container) return undefined;
		updateScrollState();
		return setupScrollListener(container, scrollTimeoutRef, updateActiveIndex);
	}, [itemCount, updateActiveIndex, updateScrollState]);

	const scrollToItem = useCallback((index: number) => {
		const item = itemRefs.current[index];
		if (item && scrollContainerRef.current) {
			item.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
		}
	}, []);

	const { handleScrollLeft, handleScrollRight } = useMemo(
		() => createScrollHandlers(activeIndex, itemCount, scrollToItem),
		[activeIndex, itemCount, scrollToItem],
	);

	const setItemRef = useCallback(
		(index: number) => (el: HTMLDivElement | null) => {
			itemRefs.current[index] = el;
		},
		[],
	);

	return {
		scrollContainerRef,
		itemRefs,
		activeIndex,
		canScrollLeft,
		canScrollRight,
		needsScroll,
		handleScrollLeft,
		handleScrollRight,
		setItemRef,
	};
}
